import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

// import { useTheme } from 'utils/Theme';
import { CeoSubmittedFormType } from 'models/CeoSubmittedForm';
import { EnrollmentFormModel } from 'models/EnrollmentForm';
import { SurveyType } from 'models/Survey';
import { Summary } from 'routes/Dashboard/Diagnostic/Diagnostic.props';

import { UnfinishedEmailProps } from '../UnfinishedEmail/UnfinishedEmail.view';
import * as d3 from 'd3';


export interface HeatMapProps {
  enrollmentForm?: EnrollmentFormModel;
  survey?: SurveyType;
  summary: Summary;
  ceoAnswer?: CeoSubmittedFormType;
  unsubmittedEmails: string[];
  getColorOfText: (percent: number) => string;
  unfinishedEmailProps?: UnfinishedEmailProps;
  hideHeader?: boolean;
}


// MARK:- Helper Functions
function getColorOfText(percent: number)
{
  if (percent < 64.5) {
    return '#f56757'; //'#ef4937'; //red
  }

  if (percent < 74.5) {
    return '#ffcf24'; //gold
  }

  if (percent < 80.0) {
    return '#8aaf74'; //green
  }

  return '#8aaf74';
}

interface BorderRadius {
  topRight?: number,
  bottomRight?: number,
  bottomLeft?: number,
  topLeft?: number
}

interface RectProps {
  x: number,
  y: number,
  width: number,
  height: number,
  fill: string,
  borderRadius?: BorderRadius
}

const Rect: FunctionComponent<RectProps> = (props: RectProps) => {
  const { x, y, width, height, fill, borderRadius } = props;
  const { topRight, bottomRight, bottomLeft, topLeft } = {
    ... { topRight: 0, bottomRight: 0, bottomLeft: 0, topLeft: 0},
    ... borderRadius,
  };
  
  const d = "M" + x + "," + y
    + "h" + (width - topRight)
    + "a" + topRight + "," + topRight + " 0 0 1 " + topRight + "," + topRight
    + "v" + (height - 2 * bottomRight)
    + "a" + bottomRight + "," + bottomRight + " 0 0 1 " + -bottomRight + "," + bottomRight
    + "h" + (bottomLeft - width)
    + "a" + bottomLeft + "," + bottomLeft + " 0 0 1 " + -bottomLeft + "," + -bottomLeft
    + 'v' + (2 * topLeft - height)
    + "a" + topLeft + "," + topLeft + " 0 0 1 " + topLeft + "," + -topLeft
    + "z";
  return <path d={d} fill='#000000'/>
}

const AssessmentsBar: FunctionComponent<any> = ({ xScale, yScale, data, xOffset, yOffset }) => {
  const { title, metric } = data;
  const width: number = xScale(metric);
  const height: number = yScale.bandwidth() / 2;
  const x = xScale(0);
  const y = (yScale(title) ?? 0);
  const path=`M${x + xOffset},${y + yOffset + height / 2} h${width - 8} a8,8 0 0 1 8,8 v${height - 8 -9} a8,8 0 0 1 -8,8 h${8 - width} z`;

  return (<g>
    { width && <path d={path} fill={getColorOfText(metric)} /> }
    <rect x={x} y={y + yOffset + height / 2} width={xOffset} height={height} fill='#eff2f4'/>
    <text x={x + 5} y={y + yOffset + height + 5} fill='#194261' fontSize={14} fontWeight='bold'>
      {title}
    </text>
    <text x={x + xOffset + (width > 0 ? width / 2 - 10 : 5) } y={y + yOffset + height + 5} fill='#3a3a3a' fontSize={14} fontWeight='bold'>
      {metric.toFixed(2)} %
    </text>
  </g>);
}

const AssessmentsBarChart: FunctionComponent<any> = ({ data }: { data: Array<any> }) => {
  const width = 800;
  const height = 500;
  const lblWidth = 200;
  const titleHeight = 50;
  const footerHeight = 100;

  const xScale = d3.scaleLinear()
    .domain([0, 100]) //TODO: calculate or take as part of properties
    .range([0, width - lblWidth]);
  const yScale = d3.scaleBand()
    .domain(data.map(x => x.title)) //TODO: calculate or take as part or properties
    .range([0, height - (titleHeight + footerHeight)]); //invert arguments to render an inverted list of ticks (domain)

  const fx = (index: number): number => 200 * index;
  const xTickPosition = (x: number): number => {
    const pos = xScale(x) - 5;
    if (x === 100) return pos - 30;
    return pos;
  }

  const headerPath = `M0,0 h${width - 8} a8,8 0 0 1 8,8 v${titleHeight - 8} h${-width} v${8 - titleHeight} a8,8 0 0 1 8,-8 z`;
  return (
    <>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        id='assessment-chart'
        viewBox={'0 0 ' + width + ' ' + height}
      >
        <g>
          <rect
            x={xScale(64.5) + lblWidth}
            y={titleHeight}
            width={xScale(74.5) - xScale(64.5)}
            height={height - titleHeight - footerHeight}
            fill='#ffcf24'
            opacity='0.4'
          />
          <rect
            x={xScale(74.5) + lblWidth}
            y={titleHeight}
            width={xScale(100) - xScale(74.5)}
            height={height - titleHeight - footerHeight}
            fill='#8aaf74'
            opacity='0.4'
          />

          <path d={headerPath} fill='#194261'/>
          <text x={20} y={25} fill='#ffcf24' fontSize={12} fontWeight='bold'>
            Focus Areas
          </text>

          {
            [
              { value:  0.0, label: '< 64.5%', },
              { value: 64.5, label: '64.5% - 74.5%', },
              { value: 74.5, label: '> 74.5%', },
              { value: 80.0, label: '> 80.0% - World Class', },
            ].map(({value, label}, index) => {
              return (<g id='chart-legend' key={index}>
                <rect
                  x={fx(index)}
                  y={height - footerHeight / 2 - 15}
                  width={20}
                  height={20}
                  rx={4}
                  ry={4}
                  fill={getColorOfText(value)}
                />
                <text
                  x={fx(index) + 28}
                  y={height - footerHeight / 2}
                  fontSize={12}
                  fontWeight={550}
                  fill='#194261'
                >
                  {label}
                </text>
              </g>);
            })
          }

          <g id='x-axis'>
            {d3.ticks(0, 100, 10).map(
              index => {
                return (<text
                  key={index}
                  x={xTickPosition(index) + lblWidth}
                  y={25}
                  fill='#ffcf24'
                  fontSize={11}
                  fontWeight='550'
                >
                  {index}%
                </text>);
              }
            )}
          </g>

          <g id='chart-data'>
            {data.map(d => <AssessmentsBar key={d.value} xScale={xScale} yScale={yScale} data={d} xOffset={lblWidth} yOffset={titleHeight} />)}
          </g>

          <g>
            <line
              x1={lblWidth + xScale(80)}
              y1={titleHeight + 1}
              x2={lblWidth + xScale(80)}
              y2={height - footerHeight}
              strokeWidth={1}
              stroke="rgba(0, 0, 0, 0.2)"
              strokeDasharray="5,3"
            />
          </g>
        </g>
      </svg>
    </>
  );
};


const HeatMapChart = (props: HeatMapProps): JSX.Element => {
  // const theme = useTheme();

  const keys = Object.keys(props.summary);

  const data = keys.map((key) => ({
    name: key,
    ...props.summary[key],
    color: props.getColorOfText(props.summary[key].categoryScore),
    categoryScoreStr: props.summary[key].categoryScore.toFixed(2) + '%',
  }));


  const XAxis: FunctionComponent<any> = (props: any) => {
    const { xScale } = props;
    const unit = props.unit ?? '';
    const ticks: Array<{ value: number, xOffset: number }> = xScale.ticks().map((value: number) => ({
      value,
      xOffset: xScale(value)
    }));

    return (
      <svg
        style={{
          color: '#ffcf24',
          height: '3.2em',
          fontSize: '1.2em',
          width: props.width,
          padding: '8px 0 0 0',
          background: '#194261',
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px'
        }}
      >
        <g>
          <g transform={`translate(60, 0)`}>
            <text 
              fill='#ffcf24'
              fontWeight={600}
              style={{
                fontSize: '14px',
                textAnchor: 'middle',
                transform: 'translateY(20px)'
              }}
            >
              Focus Areas
            </text>
          </g>
          {ticks.map(({ value, xOffset }) => (
            <g key={value} transform={`translate(${200 + xOffset}, 0)`}>
              <text 
                key={value} 
                fill='#ffcf24'
                fontWeight={600}
                style={{
                  fontSize: '14px',
                  textAnchor: 'middle',
                  transform: 'translateY(20px)'
                }}
              >
                {value}{unit}
              </text>
            </g>
          ))}
        </g>
      </svg>
    );
  };

  const HorizontalBarChart: FunctionComponent<any> = (props: any) => {
    const margin = 225;
    const xScale = d3.scaleLinear()
      .domain([0, 100])
      .range([0, props.width - margin]);
  
    return (
    <div
      style={{
        width: props.width,
      }}
    >
      <div
        style={{
          width: props.width,
          boxShadow: '2.4px 2.4px 3.2px rgba(0, 0, 0, 0.15)',
          margin: '0 0 24px 0',
          background: 'linear-gradient(to right, rgba(255, 255, 255, 1) 65%, rgba(255, 207, 36, 0.2) 65% 75%, rgba(138, 175, 116, 0.2) 75%)'
        }}
      >
        <div style={{ width: props.width }}>
          <XAxis xScale={xScale} unit='%' width={props.width} />
        </div>
        {data.map((entry) => (
          <div style={{
            padding: '12px 0',
            width: props.width,
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap'
          }}>
            <div
              style={{
                width: '200px',
                height: '40px',
                lineHeight: '40px',
                background: '#eff2f4',
                padding: '0 8px'
              }}
            >
              {entry.name}
            </div>
            <div
              style={{
                width: xScale(entry.categoryScore),
                height: '40px',
                lineHeight: '40px',
                background: props.getColorOfText(entry.categoryScore),
                borderTopRightRadius: '8px',
                borderBottomRightRadius: '8px'
              }}
            >
              <div
                style={{
                  margin: '0 auto;',
                  textAlign: 'center',
                  fontWeight: 600,
                  color: '#3a3a3a'
                }}
              >
                {entry.categoryScoreStr}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className='heatmap-legend-container'>
        <div>
          <div className='heatmap-sample-box' style={{ background: props.getColorOfText(0) }}></div>&lt; 64.50%
        </div>
        <div>
          <div className='heatmap-sample-box' style={{ background: props.getColorOfText(70) }}></div>64.5% - 74.4%
        </div>
        <div>
          <div className='heatmap-sample-box' style={{ background: props.getColorOfText(75) }}></div>&gt; 74.5%
        </div>
        <div>
          <div className='heatmap-sample-box' style={{ background: props.getColorOfText(81) }}></div>&gt; 80% - World Class
        </div>
      </div>
    </div>
    );
  };


  return (<>
    <HorizontalBarChart
      width={1024}
      height={900}
      getColorOfText={props.getColorOfText}
    />
  </>);
};

export { HeatMapChart, AssessmentsBarChart };
